import React from "react";
import PropTypes from "prop-types";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { formatMoney } from "../../Budgets/formatters";

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between py-1.5">
    <span className="text-xs text-text-2">{label}</span>
    <span className="text-sm font-medium text-text">{value ?? "—"}</span>
  </div>
);
Row.propTypes = { label: PropTypes.string, value: PropTypes.node };

/**
 * impactAmount = signed change (delta). previousAmount = current budget amount.
 * Proposed = previous + impactAmount.
 */
const FinancialImpactPanel = ({
  impactAmount,
  impactCurrency,
  previousAmount,
}) => {
  const currency = impactCurrency || "EUR";
  const hasPrevious = previousAmount != null;
  const hasDelta = impactAmount != null;

  const proposedAmount =
    hasPrevious && hasDelta ? previousAmount + impactAmount : null;
  const hasProposed = proposedAmount != null;

  const formattedPrevious = hasPrevious
    ? formatMoney(Math.round(previousAmount * 100), currency)
    : null;

  const formattedProposed = hasProposed
    ? formatMoney(Math.round(proposedAmount * 100), currency)
    : null;

  let delta = impactAmount;
  let percentChange = null;
  if (hasDelta && hasPrevious && previousAmount !== 0) {
    percentChange = (impactAmount / previousAmount) * 100;
  }

  let deltaPrefix = "";
  if (delta != null && delta >= 0) deltaPrefix = "+";
  const formattedDelta =
    delta == null
      ? null
      : `${deltaPrefix}${formatMoney(Math.round(Math.abs(delta) * 100), currency)}`;

  let formattedPercent = null;
  if (percentChange != null) {
    const sign = percentChange >= 0 ? "+" : "";
    formattedPercent = `${sign}${percentChange.toFixed(1)}%`;
  }

  const hasData = formattedPrevious || formattedProposed || formattedDelta;

  return (
    <div className="bg-card border border-card-bd rounded-card p-5">
      <div className="flex items-center gap-2 mb-4">
        <FontAwesomeIcon
          icon={icon({ name: "chart-mixed", family: "sharp", style: "light" })}
          className="text-text-2 text-sm"
        />
        <h3 className="text-sm font-semibold text-text">
          Financial Impact
        </h3>
      </div>

      {hasData ? (
        <div>
          <div className="divide-y divide-border">
            <Row label="Previous Budget" value={formattedPrevious} />
            <Row label="Proposed Budget" value={formattedProposed} />
          </div>
          {(formattedDelta || formattedPercent) && (
            <div className="divide-y divide-border mt-3 pt-1 border-t border-border">
              {formattedDelta && <Row label="Change" value={formattedDelta} />}
              {formattedPercent && (
                <Row label="Change (%)" value={formattedPercent} />
              )}
            </div>
          )}
        </div>
      ) : (
        <p className="text-sm text-text-3">
          Impact information unavailable
        </p>
      )}
    </div>
  );
};

FinancialImpactPanel.propTypes = {
  impactAmount: PropTypes.number,
  impactCurrency: PropTypes.string,
  previousAmount: PropTypes.number,
};

export default FinancialImpactPanel;
